import React from 'react';
import { MusicDistributionClientContext } from '../../context/MusicDistributionClientContext';
import { FieldValidationErrorsType, showErrorFlush } from '../../../common/form/FrekulForm';
import MusicAlbumEditForm1ImageConfirmPopup from './MusicAlbumEditForm1ImageConfirmPopup';
import MusicAlbumEditForm1View from './MusicAlbumEditForm1View';
import { MusicAlbumArtistEntity, MusicAlbumEntity, MusicAlbumTrackEntity } from '../../types/MusicAlbumEntityTypes';
import { MusicEntity } from '../../types/MusicEntityTypes';
import useCustomForm, { validateField } from '../../../common/hooks/CustomForm';
import ValidationUtility from '../../../common/utility/ValidationUtility';
import { CustomUploadConfirmDeletionPopup } from '../../../common/components/CustomUpload';

export default function MusicAlbumEditForm1({
  musicAlbum,
  genreOptions,
} : {
  musicAlbum: MusicAlbumEntity & {
    music_album_artists: MusicAlbumArtistEntity[],
    music_album_tracks: (MusicAlbumTrackEntity & {
      music: MusicEntity,
    })[],
  },
  genreOptions: {
    value: string
    text: string,
  }[],
}) {
  type PopupName = 'imageConfirm'|'imageDeleteConfirm';
  type AjaxName = 'update';

  const { createMusicAlbum, updateMusicAlbum } = React.useContext(MusicDistributionClientContext);
  const [popup, setPopup] = React.useState<PopupName|null>(null);
  const [ajax, setAjax] = React.useState<AjaxName|null>(null);

  type FieldName = 'is_multiple'|'title'|'title_kana'|'title_en'|'genre'|'image_delete';
  type ValuesType = { [name in FieldName]: string };
  type ErrorsType = { [name in FieldName]?: FieldValidationErrorsType };
  const [values, errors, setValues, setErrors] = useCustomForm<ValuesType, ErrorsType, { name: FieldName }>({
    defaultValue: {
      is_multiple: (musicAlbum.is_multiple === null || typeof musicAlbum.is_multiple === 'undefined')
        ? ''
        : (musicAlbum.is_multiple ? '1' : '0'),
      title: musicAlbum.title || '',
      title_kana: musicAlbum.title_kana || '',
      title_en: musicAlbum.title_en || '',
      genre: musicAlbum.genre || '',
      image_delete: '0',
    },
    validate(values, options, errors): ErrorsType {
      const name = options.name;
      const value = values[name];
      const error = validateField([
        ['is_multiple', 'notEmpty', 'このリリースに含める曲数を選択してください。', (value) => value.length > 0],
        ['title', 'notEmpty', 'アーティストが入力されていません', (value) => values.is_multiple === '0' || value.length > 0],
        ['title_kana', 'notEmpty', 'アーティストが入力されていません', (value) => values.is_multiple === '0' || value.length > 0],
        ['title_kana', 'isValidKana', 'アーティスト名をひらがなで入力してください', (value) => values.is_multiple === '0' || ValidationUtility.isValidTextKana(value)],
        ['title_en', 'notEmpty', 'アーティストが入力されていません', (value) => values.is_multiple === '0' || value.length > 0],
      ], name, value);
      return { ...errors, [name]: error };
    },
  });

  type FileFieldName = 'image';
  type FileValuesType = { [name in FileFieldName]: FileList|null };
  type FileErrorsType = { [name in FileFieldName]?: FieldValidationErrorsType };
  const [fileValues, fileErrors, setFileValues, setFileErrors] = useCustomForm<FileValuesType, FileErrorsType, { name: FileFieldName }>({
    defaultValue: {
      image: null,
    },
    validate(values, options, fileErrors): FileErrorsType {
      const name = options.name;
      const value = values[name];
      const uploaded = musicAlbum.image_url !== null;
      const error = validateField<FileList|null>([
        ['image', 'notEmpty', 'ジャケット画像を選択してください。', (value) => uploaded || value !== null],
        ['image', 'type', 'jpgまたはpngを指定してください。', (value) => value === null || /\.(jpg|jpeg|png)$/i.test(value[0].name) ],
      ], name, value);
      return { ...fileErrors, [name]: error };
    },
  });

  type MusicAlbumArtistFieldName = 'name'|'name_kana'|'name_en';
  type MusicAlbumArtistValuesType = { [name in MusicAlbumArtistFieldName]: string }[];
  type MusicAlbumArtistErrorsType = { [name in MusicAlbumArtistFieldName]?: FieldValidationErrorsType }[];

  const [
    musicAlbumArtistValues,
    musicAlbumArtistErrors, 
    setMusicAlbumArtistValues,
    setMusicAlbumArtistErrors,
  ] = useCustomForm<
    MusicAlbumArtistValuesType,
    MusicAlbumArtistErrorsType,
    { type: 'set', index: number, name: MusicAlbumArtistFieldName }|{ type: 'add' }
  >({
    defaultValue: 
      (musicAlbum.music_album_artists && musicAlbum.music_album_artists.length > 0)
        ? musicAlbum.music_album_artists
        : [{ name: '', name_en: '', name_kana: '' }],
    validate(valuesArray, options, musicAlbumArtistErrors): MusicAlbumArtistErrorsType {
      if (options.type === 'add') {
        return musicAlbumArtistErrors || [];
      }
      const index = options.index;
      const name = options.name;
      const values = valuesArray[index];
      const value = values[name];
      const isEmptyValues = (values.name === '' && values.name_kana === '' && values.name_en === '');
      const error = validateField([
        ['name', 'notEmpty', 'アーティストが入力されていません', (value) => isEmptyValues || value.length > 0],
        ['name', 'duplicate', '同じアーティスト名が入力されています', (value) => isEmptyValues || value.length === 0 || valuesArray.filter((values) => values.name === value).length === 1],
        ['name_kana', 'notEmpty', 'アーティストが入力されていません', (value) => isEmptyValues || value.length > 0],
        ['name_kana', 'isValidKana', 'アーティスト名をひらがなで入力してください', ValidationUtility.isValidTextKana],
        ['name_kana', 'duplicate', '同じアーティスト名が入力されています', (value) => isEmptyValues || value.length === 0 || valuesArray.filter((values) => values.name_kana === value).length === 1],
        ['name_en', 'notEmpty', 'アーティストが入力されていません', (value) => isEmptyValues || value.length > 0],
        ['name_en', 'duplicate', '同じアーティスト名が入力されています', (value) => isEmptyValues || value.length === 0 || valuesArray.filter((values) => values.name_en === value).length === 1],
        ['name_en', 'isValidEn', 'アーティスト名を半角英数字で入力してください', ValidationUtility.isValidTextEn],
      ], name, value);
      const errorsArray = valuesArray.map((_, index) => musicAlbumArtistErrors?.[index] || {});
      errorsArray[index] = { ...errorsArray[index] };
      errorsArray[index][name] = error;
      return errorsArray;
    }
  })

  React.useEffect(() => {
    if (ajax === 'update') {
      const postData = { ...values, ...fileValues, music_album_artists: musicAlbumArtistValues };
      if (postData.is_multiple === '0') {
        postData.title = '';
        postData.title_kana = '';
        postData.title_en = '';
      }
      if (!musicAlbum.id) {
        createMusicAlbum(postData).then((responseData) => {
          setAjax(null);
          setErrors(responseData.validationErrors || {});
          setFileErrors(responseData.validationErrors || {});
          setMusicAlbumArtistErrors(responseData.validationErrors?.music_album_artists as unknown as FieldValidationErrorsType[]);
          if (responseData.result && responseData.music_album_id !== null) {
            document.location.href = `/music_albums/select_tracks/${responseData.music_album_id}`;
          } else {
            showErrorFlush('入力内容に不備があります。');
          }
        });
      } else {
        updateMusicAlbum(musicAlbum.id, postData).then((responseData) => {
          setAjax(null);
          setErrors(responseData.validationErrors || {});
          setFileErrors(responseData.validationErrors || {});
          setMusicAlbumArtistErrors(responseData.validationErrors?.music_album_artists as { [key:string]: FieldValidationErrorsType }[]);
          if (responseData.result) {
            document.location.href = `/music_albums/select_tracks/${musicAlbum.id}`;
          } else {
            showErrorFlush('入力内容に不備があります。');
          }
        });
      }
    }
  }, [ajax]);

  const handleConfirm = (callback: ((musicAlbumId: number) => void)|null) => {
    if (fileValues.image) {
      setPopup('imageConfirm');
    } else {
      setAjax('update');
    }
  };

  return (
    <>
      <MusicAlbumEditForm1View
        getFile={(name) => fileValues[name]}
        setFile={(name, files) => {
          if (!musicAlbum.id) {
            setFileValues({ ...fileValues, [name]: files }, { name });
          } else {
            setValues({ ...values, image_delete: '0' }, { name: 'image_delete' });
            setFileValues({ ...fileValues, [name]: files }, { name });
          }
        }}
        deleteFile={(name) => {
          if (!musicAlbum.id) {
            setFileValues({ ...fileValues, [name]: null }, { name });
          } else {
            if (fileValues[name] !== null) {
              setFileValues({ ...fileValues, [name]: null }, { name });
            } else {
              setPopup('imageDeleteConfirm');
            }
          }
        }}
        getValue={(name) => {
          if (typeof name === 'string') {
            return values[name];
          } else {
            return musicAlbumArtistValues[name[1]][name[2]];
          }
        }}
        setValue={(name, value) => {
          if (typeof name === 'string') {
            setValues({ ...values, [name]: value }, { name });
          } else {
            const newMusicAlbumArtistValues = [...musicAlbumArtistValues];
            newMusicAlbumArtistValues[name[1]] = { ...newMusicAlbumArtistValues[name[1]] };
            newMusicAlbumArtistValues[name[1]][name[2]] = value;
            setMusicAlbumArtistValues(newMusicAlbumArtistValues, { type: 'set', index: name[1], name: name[2] });
          }
        }}
        getErrors={(name) => {
          if (name === 'image') {
            return fileErrors?.[name] || {};
          } else if (typeof name === 'string') {
            return errors?.[name] || {};
          } else {
            return musicAlbumArtistErrors?.[name[1]]?.[name[2]] || {};
          }
        }}
        getNumberOfItems={(collection) => musicAlbumArtistValues.length}
        addItem={(name) => {
          setMusicAlbumArtistValues([
            ...musicAlbumArtistValues,
            { name: '', name_kana: '', name_en: '' },
          ], { type: 'add' });
        }}
        musicAlbum={musicAlbum}
        genreOptions={genreOptions}
        onBack={() => {
          document.location.href = '/music_albums';
        }}
        onNext={() => {
          handleConfirm(null);
        }}
        onStep={(callback) => {
          handleConfirm(callback);
        }}
      />
      {popup === 'imageConfirm' && (
        <MusicAlbumEditForm1ImageConfirmPopup
          onClose={() => setPopup(null)}
          onNext={() => {
            setPopup(null);
            setAjax('update');
          }}
        />
      )}
      {popup === 'imageDeleteConfirm' && (
        <CustomUploadConfirmDeletionPopup
          type="image"
          onDelete={() => {
            setValues({ ...values, image_delete: '1' }, { name: 'image_delete' });
            setFileValues({ ...fileValues, image: null }, { name: 'image' });
            setPopup(null);
          }}
          onCancel={() => {
            setPopup(null);
          }}
        />
      )}
    </>
  );
}

